import type { UseTRPCMutationResult } from '@trpc/react-query/shared';
import { getRecordValue, isRecord, isRecordLike } from '@/lib/utils/type-guards';

const isFunction = (value: unknown): value is (...arguments_: unknown[]) => unknown => typeof value === 'function';

export const deriveMutationLabel = (factoryOwner: unknown): string => {
  if (!isRecordLike(factoryOwner)) {
    return 'trpc.mutation';
  }

  const pathCandidates: unknown[] = [
    getRecordValue(factoryOwner, 'path'),
    getRecordValue(factoryOwner, 'procedurePath'),
    getRecordValue(factoryOwner, 'routerPath'),
  ];

  const defValue = getRecordValue(factoryOwner, '_def');
  const def = isRecord(defValue) ? defValue : null;
  if (def) {
    pathCandidates.push(getRecordValue(def, 'path'), getRecordValue(def, 'procedurePath'));
  }

  const metaValue = getRecordValue(factoryOwner, 'meta');
  const meta = isRecord(metaValue) ? metaValue : null;
  if (meta) {
    pathCandidates.push(getRecordValue(meta, 'path'));
  }

  const derived = pathCandidates.find(
    (candidate): candidate is string => typeof candidate === 'string' && candidate.length > 0
  );

  if (!derived) {
    return 'trpc.mutation';
  }

  return derived.startsWith('trpc.') ? derived : `trpc.${derived}`;
};

export type MutationOptions = Record<string, unknown>;

export type MutationFactory = {
  useMutation: (options?: MutationOptions) => unknown;
};

export type TrpcMutationAdapter = UseTRPCMutationResult<unknown, unknown, unknown, unknown>;

function isTrpcMutationAdapter(value: unknown): value is TrpcMutationAdapter {
  if (!isRecordLike(value)) {
    return false;
  }
  const mutate = getRecordValue(value, 'mutate');
  const mutateAsync = getRecordValue(value, 'mutateAsync');
  return isFunction(mutate) && isFunction(mutateAsync);
}

export function useTrpcMutationAdapter<TFactory extends MutationFactory>(
  factoryOwner: TFactory,
  labelOrOptions?: string | MutationOptions,
  maybeOptions?: MutationOptions
): TrpcMutationAdapter {
  const hasExplicitLabel = typeof labelOrOptions === 'string';
  const options: MutationOptions | undefined = hasExplicitLabel ? maybeOptions : labelOrOptions;
  const label = typeof labelOrOptions === 'string' ? labelOrOptions : deriveMutationLabel(factoryOwner);

  const mutationResult = factoryOwner.useMutation(options);
  if (!isRecordLike(mutationResult)) {
    throw new TypeError(`${label} mutate helper unavailable`);
  }
  const mutate = getRecordValue(mutationResult, 'mutate');
  if (!isFunction(mutate)) {
    throw new TypeError(`${label} mutate helper unavailable`);
  }
  const mutateAsync = getRecordValue(mutationResult, 'mutateAsync');
  if (!isFunction(mutateAsync)) {
    throw new TypeError(`${label} mutateAsync helper unavailable`);
  }

  if (!isTrpcMutationAdapter(mutationResult)) {
    throw new TypeError(`${label} mutation result unexpected shape`);
  }

  return mutationResult;
}

export { useTrpcMutationAdapter as createTrpcMutationAdapter };
